/**
 * MarketingCampaignsPage — Phase 8 Prompts 15+18+20 implementation.
 *
 * Per audit 05B_CAMPAIGN_MANAGER.md.
 *
 * 4-step wizard: Audience → Content → Schedule → Review.
 * Email channel first (Prompt 15); SMS added Prompt 18; push added Prompt 20.
 * Marketing-only enforcement (kind='nurture' constraint at API).
 */

import React, { useState } from "react";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Alert,
  Skeleton,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PillarPageShell from "../shared/PillarPageShell";
import {
  ActionButton,
  StatusPill,
  Wizard,
  type WizardStep,
} from "../../ops";
import CampaignTemplatePickerDialog from "./CampaignTemplatePickerDialog";
import { EmptyState, HelpTip } from "../../help";
import { useSmartDefaults } from "../../../hooks/useSmartDefaults";
import apiService from "../../../services/api";
import {
  buildCampaignHtmlContent,
  prefillFromEmailTemplate,
  type SelectedEmailTemplate,
} from "./campaignStarterUtils";

interface AudiencePickerRow {
  id: string;
  slug: string;
  name: string;
  last_count: number | null;
  /** Email-ready members (active + email); matches send resolution. */
  reachable_count?: number | null;
  is_active: boolean;
}

function audienceReachableCount(a: AudiencePickerRow): number {
  if (a.reachable_count != null) return a.reachable_count;
  return a.last_count ?? 0;
}

interface CampaignFormState {
  channel: "email" | "sms" | "push";
  audienceId: string | null;
  audienceName: string;
  audienceCount: number;
  templateId: string | null;
  templateName: string | null;
  subjectLine: string;
  bodyText: string;
  /** Branded layout from campaign starter; merged with bodyText at send. */
  bodyHtmlTemplate: string | null;
  smsBody: string;
  pushTitle: string;
  pushBody: string;
  sendMode: "now" | "scheduled";
  scheduledAt: string;
  variantsEnabled: boolean;
  /** Ad-hoc only: skip marketing frequency cap for this send. */
  bypassFrequencyLimits: boolean;
}

/**
 * Human-readable stamp for the default send name. The old default was a raw
 * ISO string ("Ad-hoc 2026-08-21T15:45:12.883Z"), which is what an operator
 * then had to scan for in the campaign list.
 */
function humanTimestamp(d: Date = new Date()): string {
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Dynamic segments from /api/admin/segments must use dyn:<slug> at send time. */
function toTargetSegment(slug: string | null): string | null {
  if (!slug) return null;
  if (slug.startsWith("dyn:")) return slug;
  return `dyn:${slug}`;
}

const initialFormState: CampaignFormState = {
  channel: "email",
  audienceId: null,
  audienceName: "",
  audienceCount: 0,
  templateId: null,
  templateName: null,
  subjectLine: "",
  bodyText: "",
  bodyHtmlTemplate: null,
  smsBody: "",
  pushTitle: "",
  pushBody: "",
  sendMode: "now",
  scheduledAt: "",
  variantsEnabled: false,
  bypassFrequencyLimits: false,
};

// ─────────────────────────────────────────────────────────────────────────
// List view
// ─────────────────────────────────────────────────────────────────────────
const CampaignListView: React.FC<{ adhocMode?: boolean }> = ({
  adhocMode = false,
}) => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // A failed list fetch is NOT an empty list. Without this the catch swallowed
  // the error and the page invited the operator to "create your first
  // campaign" over a shelf that may be full.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [chooserOpen, setChooserOpen] = useState(false);

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        // Compose & Send shows ALL sends (campaign + ad-hoc); the legacy adhoc view filters.
        const res: any = await apiService.get(
          `/api/admin/email-marketing/campaigns?limit=100${adhocMode ? "&sub_kind=adhoc" : ""}`,
        );
        if (!mounted) return;
        const rows = res?.data?.campaigns ?? res?.campaigns ?? [];
        const isAdhoc = (r: any) => r.sub_kind === "adhoc";
        setCampaigns(adhocMode ? rows.filter(isAdhoc) : rows);
      } catch (e: unknown) {
        if (!mounted) return;
        console.error("Load campaigns failed", e);
        setCampaigns([]);
        setLoadError(
          e instanceof Error ? e.message : "Failed to load campaigns",
        );
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [adhocMode, reloadKey]);

  const handleTemplateSelect = (template: SelectedEmailTemplate) => {
    navigate("new", { state: { emailTemplate: template } });
  };

  const handleBlankCanvas = () => {
    navigate("new", { state: {} });
  };

  const filteredRows = campaigns.filter((c) => {
    if (filter !== "all" && c.status !== filter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (!c.name?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <PillarPageShell
      pillar="marketing"
      title={adhocMode ? "Ad-hoc Composer" : "Compose & Send"}
      subtitle={
        adhocMode
          ? "Immediate-send variant of Campaign Manager. Send Now default. Marketing-only per audit."
          : "Create a marketing email and send it to a tracked audience — now or scheduled. CAN-SPAM applies."
      }
      rightActions={
        <ActionButton
          verb="duplicate"
          labelOverride={adhocMode ? "+ Compose Ad-hoc" : "+ New Campaign"}
          onClick={() => setChooserOpen(true)}
          showIcon={false}
          variantOverride="primary"
        />
      }
    >
      {!adhocMode && (
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs value={filter} onChange={(_, v) => setFilter(v)}>
            <Tab value="all" label="All" />
            <Tab value="draft" label="Draft" />
            <Tab value="scheduled" label="Scheduled" />
            <Tab value="sending" label="Sending" />
            <Tab value="sent" label="Sent" />
          </Tabs>
        </Box>
      )}

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search campaigns…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Card>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          {loading ? (
            <Box sx={{ p: 2 }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} height={48} />
              ))}
            </Box>
          ) : loadError ? (
            <Alert
              severity="error"
              sx={{ m: 2 }}
              action={
                <ActionButton
                  verb="refresh"
                  labelOverride="Retry"
                  size="small"
                  showIcon={false}
                  onClick={() => setReloadKey((k) => k + 1)}
                />
              }
            >
              <strong>Couldn&apos;t load campaigns.</strong> {loadError} — this
              list is unknown, not empty.
            </Alert>
          ) : filteredRows.length === 0 ? (
            <EmptyState
              surface="campaigns"
              onCreate={() => setChooserOpen(true)}
            />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Audience</TableCell>
                  <TableCell align="right">Sent</TableCell>
                  <TableCell align="right">Open %</TableCell>
                  <TableCell align="right">Click %</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((c: any) => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {c.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {c.subject_line}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusPill status={c.status} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {c.target_segment ?? "—"} ({c.total_recipients ?? 0})
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{c.total_sent ?? 0}</TableCell>
                    <TableCell align="right">
                      {/* total_opened is nullable — without the ?? 0 this
                          printed "NaN%" (the click cell next door already
                          guarded, this one did not). */}
                      {c.total_sent > 0
                        ? `${(((c.total_opened ?? 0) / c.total_sent) * 100).toFixed(1)}%`
                        : "—"}
                    </TableCell>
                    <TableCell align="right">
                      {c.total_sent > 0
                        ? `${(((c.total_clicked ?? 0) / c.total_sent) * 100).toFixed(1)}%`
                        : "—"}
                    </TableCell>
                    <TableCell align="right">
                      <ActionButton
                        verb="duplicate"
                        labelOverride="Clone"
                        size="small"
                        showIcon={false}
                        onClick={() => navigate("new", { state: { clone: c } })}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CampaignTemplatePickerDialog
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        onSelect={handleTemplateSelect}
        onBlankCanvas={handleBlankCanvas}
        title={
          adhocMode ? "Choose template for ad-hoc send" : "Choose template for campaign"
        }
      />
    </PillarPageShell>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Wizard view (shared between Campaigns and Ad-hoc Composer)
// ─────────────────────────────────────────────────────────────────────────
const CampaignWizardView: React.FC<{ adhocMode?: boolean }> = ({
  adhocMode = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdhocComposer =
    adhocMode || location.pathname.includes("/marketing/adhoc");
  const [searchParams] = useSearchParams();
  const audienceFromDeepLink = searchParams.get("audience");

  // `defaults` was destructured and never read; only recordChoice is used.
  const { recordChoice } = useSmartDefaults();

  const locationState = location.state as {
    emailTemplate?: SelectedEmailTemplate;
    clone?: {
      subject_line?: string;
      content?: string;
      target_segment?: string;
      template_id?: string;
    };
  } | null;

  const [form, setForm] = useState<CampaignFormState>(() => {
    const base: CampaignFormState = {
      ...initialFormState,
      sendMode: "now",
      audienceId: audienceFromDeepLink,
      audienceName: audienceFromDeepLink ?? "",
    };
    if (locationState?.emailTemplate) {
      const pre = prefillFromEmailTemplate(locationState.emailTemplate);
      return { ...base, ...pre };
    }
    if (locationState?.clone) {
      const seg = locationState.clone.target_segment?.replace(/^dyn:/, "");
      return {
        ...base,
        audienceId: seg ?? base.audienceId,
        templateId: locationState.clone.template_id ?? null,
        subjectLine: locationState.clone.subject_line ?? "",
        bodyText: "",
        bodyHtmlTemplate: locationState.clone.content ?? null,
      };
    }
    return base;
  });

  const [audiences, setAudiences] = useState<AudiencePickerRow[]>([]);
  const [audiencesLoading, setAudiencesLoading] = useState(true);
  const [audiencesError, setAudiencesError] = useState<string | null>(null);
  /** Live count from send pipeline (Review step); matches POST /campaigns resolution. */
  const [resolvedRecipientCount, setResolvedRecipientCount] = useState<
    number | null
  >(null);
  const [resolvedAudienceCount, setResolvedAudienceCount] = useState<
    number | null
  >(null);
  const [resolvedFrequencyCapped, setResolvedFrequencyCapped] = useState<
    number | null
  >(null);
  const [resolveCountLoading, setResolveCountLoading] = useState(false);
  const [resolveCountError, setResolveCountError] = useState<string | null>(
    null,
  );

  React.useEffect(() => {
    let mounted = true;
    setAudiencesLoading(true);
    setAudiencesError(null);
    (async () => {
      try {
        const res = await apiService.get<AudiencePickerRow[]>(
          "/api/admin/segments?active=true&limit=500",
        );
        if (!mounted) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        setAudiences(rows.filter((a) => a.is_active !== false));
      } catch (e: unknown) {
        if (!mounted) return;
        setAudiencesError(
          e instanceof Error ? e.message : "Failed to load audiences",
        );
      } finally {
        if (mounted) setAudiencesLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  React.useEffect(() => {
    if (!form.audienceId || audiences.length === 0) return;
    const match = audiences.find(
      (a) => a.slug === form.audienceId || a.id === form.audienceId,
    );
    if (!match) return;
    if (form.audienceName === form.audienceId || !form.audienceName) {
      setForm((prev) => ({
        ...prev,
        audienceName: match.name,
        audienceCount: audienceReachableCount(match),
      }));
    }
  }, [audiences, form.audienceId, form.audienceName]);

  React.useEffect(() => {
    if (!form.audienceId) {
      setResolvedRecipientCount(null);
      setResolvedAudienceCount(null);
      setResolvedFrequencyCapped(null);
      setResolveCountError(null);
      return;
    }
    let mounted = true;
    const segment = toTargetSegment(form.audienceId);
    if (!segment) return;
    setResolveCountLoading(true);
    setResolveCountError(null);
    const respectCap = !form.bypassFrequencyLimits;
    (async () => {
      try {
        const res = await apiService.get<{
          count: number;
          audience_count?: number;
          frequency_capped_count?: number;
        }>(
          `/api/admin/email-marketing/resolve-recipients?target_segment=${encodeURIComponent(segment)}&respect_frequency_limits=${respectCap}`,
        );
        if (!mounted) return;
        setResolvedRecipientCount(
          typeof res.data?.count === "number" ? res.data.count : 0,
        );
        setResolvedAudienceCount(
          typeof res.data?.audience_count === "number"
            ? res.data.audience_count
            : null,
        );
        setResolvedFrequencyCapped(
          typeof res.data?.frequency_capped_count === "number"
            ? res.data.frequency_capped_count
            : null,
        );
      } catch (e: unknown) {
        if (!mounted) return;
        setResolvedRecipientCount(null);
        setResolvedAudienceCount(null);
        setResolvedFrequencyCapped(null);
        setResolveCountError(
          e instanceof Error ? e.message : "Could not resolve recipient count",
        );
      } finally {
        if (mounted) setResolveCountLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [form.audienceId, form.bypassFrequencyLimits]);

  // NOTE: there is deliberately no auto-save here. The previous "saver" was a
  // setTimeout(80) that persisted nothing behind a TODO, while the wizard
  // showed a live "Saved N seconds ago" indicator and the page promised
  // "auto-saves as you go". Both the indicator and the promise are gone; a
  // draft is lost on navigation, and the UI no longer claims otherwise.

  const handleComplete = async (data: CampaignFormState) => {
    try {
      const sendNow = data.sendMode === "now";
      const payload: any = {
        name: sendNow
          ? `Ad-hoc ${humanTimestamp()}`
          : `Campaign ${humanTimestamp()}`,
        subject_line: data.subjectLine,
        content:
          data.channel === "email"
            ? buildCampaignHtmlContent(data.bodyText, data.bodyHtmlTemplate)
            : undefined,
        template_id: data.templateId || undefined,
        target_segment: toTargetSegment(data.audienceId),
        timing: sendNow ? "now" : "scheduled",
        scheduled_at: sendNow ? undefined : data.scheduledAt || undefined,
        kind: "nurture",
        sub_kind: sendNow ? "adhoc" : "campaign",
        respect_frequency_limits: !data.bypassFrequencyLimits,
      };
      const result: any = await apiService.post(
        "/api/admin/email-marketing/campaigns",
        payload,
        sendNow
          ? { headers: { "X-Campaign-Sub-Kind": "adhoc" } }
          : undefined,
      );
      const sentTo =
        result?.campaign?.total_recipients ??
        result?.data?.campaign?.total_recipients;
      if (sendNow && sentTo === 0) {
        throw new Error(
          "No emails were sent — this audience has 0 sendable recipients. " +
            "Open Audiences → Custom Builder → Refresh, and confirm the member has an email on file.",
        );
      }
      recordChoice({ preferredSendTime: data.scheduledAt || undefined });
      navigate("..");
    } catch (e: any) {
      window.alert(`Send failed: ${e?.message ?? "unknown error"}`);
    }
  };

  const steps: WizardStep<CampaignFormState>[] = [
    {
      id: "audience",
      title: "Audience",
      description: "Who will receive this send?",
      render: (state, set) => (
        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Audience{" "}
            <HelpTip id="audience.kind_marketing" />
          </Typography>
          {audiencesError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {audiencesError}
            </Alert>
          )}
          <Select
            fullWidth
            value={state.audienceId ?? ""}
            onChange={(e) => {
              const slug = e.target.value as string;
              const match = audiences.find((a) => a.slug === slug);
              set({
                ...state,
                audienceId: slug,
                audienceName: match?.name ?? slug,
                audienceCount: match ? audienceReachableCount(match) : 0,
              });
            }}
            displayEmpty
            size="small"
            disabled={audiencesLoading}
          >
            <MenuItem value="">— Pick an audience —</MenuItem>
            {audiencesLoading && (
              <MenuItem disabled value="__loading">
                Loading audiences…
              </MenuItem>
            )}
            {!audiencesLoading &&
              audiences.map((a) => (
                <MenuItem key={a.slug} value={a.slug}>
                  {a.name}
                  {audienceReachableCount(a) > 0
                    ? ` (${audienceReachableCount(a).toLocaleString()})`
                    : ""}
                </MenuItem>
              ))}
          </Select>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mt: 1 }}
          >
            Recipient count shown on Review step excludes unsubscribed,
            hard-bounced, and frequency-capped users.
          </Typography>
        </Box>
      ),
      validate: (s) =>
        s.audienceId ? null : "Pick an audience to continue",
    },
    {
      id: "content",
      title: "Content",
      description: "Compose your message.",
      render: (state, set) => (
        <Box>
          <ToggleButtonGroup
            value={state.channel}
            exclusive
            onChange={(_, v) => v && set({ ...state, channel: v })}
            size="small"
            sx={{ mb: 2 }}
          >
            <ToggleButton value="email">Email</ToggleButton>
            <ToggleButton value="sms">SMS</ToggleButton>
            <ToggleButton value="push">Push</ToggleButton>
          </ToggleButtonGroup>

          {state.channel === "email" && (
            <>
              {state.templateId && state.templateName && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Email design from template{" "}
                  <strong>{state.templateName}</strong>. You can edit the subject
                  and message below; the saved HTML layout is used when you send.
                </Alert>
              )}
              {state.bodyHtmlTemplate &&
                !state.templateId &&
                state.bodyHtmlTemplate.includes("{{message_body}}") && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Edit the message below — it is inserted into the email
                    layout.
                  </Alert>
                )}
              <Typography variant="body2" sx={{ mt: 1, mb: 0.5 }}>
                Subject line <HelpTip id="campaign.subject_line.helper" />
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={state.subjectLine}
                onChange={(e) =>
                  set({ ...state, subjectLine: e.target.value })
                }
                placeholder="Welcome to LifeScribe, {{user_name}}"
                sx={{ mb: 2 }}
              />
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Body (plain text fallback)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                size="small"
                value={state.bodyText}
                onChange={(e) =>
                  set({ ...state, bodyText: e.target.value })
                }
                placeholder="Compose your email body. Use {{user_name}} for personalization."
              />
            </>
          )}

          {state.channel === "sms" && (
            <>
              <Typography variant="body2" sx={{ mt: 1, mb: 0.5 }}>
                SMS body (160 chars per segment)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                size="small"
                value={state.smsBody}
                onChange={(e) => set({ ...state, smsBody: e.target.value })}
                placeholder="LifeScribe: Your story matters. Try a 30-sec voice memo: {{short_link}}"
              />
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block", mt: 0.5 }}
              >
                {state.smsBody.length} chars · TCPA quiet hours strictly
                enforced.
              </Typography>
            </>
          )}

          {state.channel === "push" && (
            <>
              <Typography variant="body2" sx={{ mt: 1, mb: 0.5 }}>
                Push title
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={state.pushTitle}
                onChange={(e) =>
                  set({ ...state, pushTitle: e.target.value })
                }
                placeholder="Your first chapter is waiting"
                sx={{ mb: 2 }}
              />
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Push body
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                value={state.pushBody}
                onChange={(e) =>
                  set({ ...state, pushBody: e.target.value })
                }
                placeholder="30 seconds of voice memo → a chapter you'll keep forever."
              />
            </>
          )}
        </Box>
      ),
      validate: (s) => {
        if (s.channel === "email" && !s.subjectLine.trim())
          return "Subject line required";
        if (s.channel === "sms" && !s.smsBody.trim())
          return "SMS body required";
        if (s.channel === "push" && !s.pushTitle.trim())
          return "Push title required";
        return null;
      },
    },
    {
      id: "schedule",
      title: "Schedule",
      description: "Send now, or schedule for later.",
      render: (state, set) => (
        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            When should this go out? <HelpTip id="campaign.send_time.helper" />
          </Typography>
          <RadioGroup
            value={state.sendMode}
            onChange={(e) =>
              set({ ...state, sendMode: e.target.value as "now" | "scheduled" })
            }
          >
            <FormControlLabel
              value="now"
              control={<Radio size="small" />}
              label="Send now — delivered as soon as you finish the wizard"
            />
            <FormControlLabel
              value="scheduled"
              control={<Radio size="small" />}
              label="Schedule for later"
            />
          </RadioGroup>
          {state.sendMode === "scheduled" && (
            <Box sx={{ mt: 1 }}>
              <TextField
                type="datetime-local"
                size="small"
                value={state.scheduledAt}
                onChange={(e) => set({ ...state, scheduledAt: e.target.value })}
                sx={{ maxWidth: 360 }}
              />
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block", mt: 1 }}
              >
                Marketing sends scheduled during 9pm–8am recipient local time
                are auto-queued to 8am next morning.
              </Typography>
            </Box>
          )}
        </Box>
      ),
      validate: (s) => {
        if (s.sendMode === "scheduled" && !s.scheduledAt) return "Pick a send time";
        return null;
      },
    },
    {
      id: "review",
      title: "Review",
      description: "Confirm before sending.",
      render: (state, set) => (
        <Box>
          <Alert severity="warning" sx={{ mb: 2 }} icon="⚠️">
            <Typography variant="body2">
              <strong>Final review.</strong> Recipient count is resolved live
              from the same rules used when you send (active users with email,
              materialized audience, frequency caps).{" "}
              <HelpTip id="campaign.review.recipient_count" />
            </Typography>
          </Alert>
          {resolveCountLoading && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Checking sendable recipients…
            </Typography>
          )}
          {resolveCountError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {resolveCountError}
            </Alert>
          )}
          {!resolveCountLoading &&
            resolvedRecipientCount === 0 &&
            state.audienceId &&
            (resolvedFrequencyCapped ?? 0) > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {/* '?? 1' invented a member count when the resolver did not
                    return one. Unknown renders as '—'. */}
                <strong>
                  {resolvedAudienceCount != null
                    ? `${resolvedAudienceCount.toLocaleString()} member(s)`
                    : "— member(s)"}
                </strong>{" "}
                in this audience, but{" "}
                <strong>all are blocked by the marketing frequency cap</strong>{" "}
                (3 emails per 7 days). Enable &quot;Bypass frequency limits&quot;
                below for a one-off test send, or wait for the cap window to
                reset.
              </Alert>
            )}
          {!resolveCountLoading &&
            resolvedRecipientCount === 0 &&
            state.audienceId &&
            (resolvedFrequencyCapped ?? 0) === 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                This audience has <strong>0 sendable recipients</strong>. Go to
                Audiences → refresh the segment, or pick a different audience
                before sending.
              </Alert>
            )}
          {isAdhocComposer && (
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={state.bypassFrequencyLimits}
                    onChange={(e) =>
                      set({
                        ...state,
                        bypassFrequencyLimits: e.target.checked,
                      })
                    }
                  />
                }
                label="Bypass frequency limits for this send (admin test only)"
              />
            </Box>
          )}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Summary
              </Typography>
              <Box sx={{ mt: 1, display: "grid", gridTemplateColumns: "1fr 2fr", gap: 1, fontSize: "0.9rem" }}>
                {state.templateName && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Template
                    </Typography>
                    <Typography variant="body2">{state.templateName}</Typography>
                  </>
                )}
                <Typography variant="body2" color="text.secondary">
                  Audience
                </Typography>
                <Typography variant="body2">
                  {state.audienceName}
                  {resolveCountLoading
                    ? " (checking…)"
                    : resolvedAudienceCount != null &&
                        resolvedAudienceCount !== resolvedRecipientCount
                      ? ` (${resolvedAudienceCount} in audience, ${resolvedRecipientCount ?? 0} will receive)`
                      : ` (${resolvedRecipientCount != null ? resolvedRecipientCount : state.audienceCount || "—"})`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Channel
                </Typography>
                <Typography variant="body2">{state.channel}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Send time
                </Typography>
                <Typography variant="body2">
                  {state.sendMode === "now"
                    ? "Send immediately"
                    : state.scheduledAt}
                </Typography>
                {state.channel === "email" && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Subject
                    </Typography>
                    <Typography variant="body2">
                      {state.subjectLine}
                    </Typography>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      ),
      validate: (s) => {
        if (!s.audienceId) return "Pick an audience";
        if (resolveCountLoading) return "Checking recipient count…";
        if (resolvedRecipientCount === 0 && !s.bypassFrequencyLimits) {
          if ((resolvedFrequencyCapped ?? 0) > 0) {
            return "Frequency cap blocks all recipients — enable bypass or wait";
          }
          return "No sendable recipients — refresh the audience or choose another";
        }
        return null;
      },
    },
  ];

  return (
    <PillarPageShell
      pillar="marketing"
      title="Compose & Send"
      subtitle="4-step wizard · Audience → Content → Schedule → Review. Drafts are not saved — finish the wizard to send or schedule."
    >
      {/* CONTROLLED wizard: `form` here is the single source of truth.
          Previously <Wizard initialState={form}> seeded a private copy that the
          page could never see, so `form` froze at its initial value: recipient
          resolution never re-ran for a newly picked audience, the 0-sendable
          guard was dead code, the audience name never hydrated into Review, and
          completeLabel always read "Send Now" even for a scheduled send. */}
      <Wizard<CampaignFormState>
        steps={steps}
        initialState={form}
        value={form}
        onChange={setForm}
        onComplete={handleComplete}
        onCancel={() => navigate("..")}
        completeLabel={form.sendMode === "now" ? "Send Now" : "Schedule"}
      />
    </PillarPageShell>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Top-level dispatcher
// ─────────────────────────────────────────────────────────────────────────
const MarketingCampaignsPage: React.FC = () => (
  <Routes>
    <Route index element={<CampaignListView />} />
    <Route path="new" element={<CampaignWizardView />} />
  </Routes>
);

export const MarketingAdhocPageImpl: React.FC = () => (
  <Routes>
    <Route index element={<CampaignListView adhocMode />} />
    <Route path="new" element={<CampaignWizardView adhocMode />} />
    {/* Ad-hoc index also handles deep-link with ?audience=... — drops you straight into wizard */}
  </Routes>
);

export default MarketingCampaignsPage;
